/**
 * Accounting Services › Bookkeeping: the register of posted transactions, the editor for a
 * transaction's lines (recategorize, split, describe, classify, memo, receipt), the QuickBooks
 * lists, and the log of what ClientFlow changed. Every save goes straight to QuickBooks.
 */
import { SuggestCategoriesButton } from "@/components/ai-buttons";
import { formatCents, parseDollars } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Lock, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ask } from "@/components/dialogs";
import { Empty, Loading, Section, Segmented } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { useMemberNames } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";

export type Account = { qboId: string; fullyQualifiedName: string; accountType: string; active: boolean };
type Row = { date: string; type: string; txnId: string | null; num: string; name: string; memo: string; account: string; split: string; amount: number; entity: Entity | null };
type Register = { asOf: string; total: number; count: number; truncated: boolean; types: string[]; closedMonths: string[]; rows: Row[] };
type Entity = "Purchase" | "Deposit" | "Bill" | "JournalEntry";
type Line = { id: string | null; description: string; amount: number; accountId: string; classId: string | null; postingType?: "Debit" | "Credit" };
type Txn = {
  entity: Entity;
  id: string;
  syncToken: string;
  date: string;
  docNumber: string;
  name: string;
  privateNote: string;
  account: { id: string; name: string } | null;
  total: number;
  lines: Line[];
  otherLines: number;
  foreignCurrency: string | null;
  closed: boolean;
  link: string;
  /** Receipts attached from ClientFlow (kept in the client's documents). */
  receipts: Array<{ documentId: string; filename: string; at: string }>;
};
export type ListRow = { id: string; syncToken: string; name: string; active: boolean; detail: Record<string, string | number | boolean | null> };

const ENTITY_LABEL: Record<Entity, string> = { Purchase: "Expense", Deposit: "Deposit", Bill: "Bill", JournalEntry: "Journal entry" };

export function useAccounts(clientId: string) {
  const { data } = useQuery<{ accounts?: Account[] }>({ queryKey: ["/api/clients/", clientId, "/qbo"] });
  return (data?.accounts ?? []).filter((a) => a.active);
}

function monthBounds(offset: number) {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  const last = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
  const iso = (x: Date) => x.toLocaleDateString("en-CA");
  return { from: iso(first), to: iso(last) };
}

/** A dollar amount kept in cents; shows what was typed while editing. */
export function MoneyInput({ value, onChange, label, disabled }: { value: number; onChange: (c: number) => void; label: string; disabled?: boolean }) {
  const [text, setText] = useState((value / 100).toFixed(2));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText((value / 100).toFixed(2));
  }, [value, editing]);
  return (
    <Input
      aria-label={label}
      inputMode="decimal"
      disabled={disabled}
      className="h-9 text-right tabular"
      value={text}
      onFocus={() => setEditing(true)}
      onBlur={() => setEditing(false)}
      onChange={(e) => {
        setText(e.target.value);
        try {
          onChange(parseDollars(e.target.value || "0"));
        } catch {
          /* keep typing */
        }
      }}
    />
  );
}

/** A dollar amount that may be negative (a credit), kept in cents. The sign can be set before the amount. */
export function SignedMoney({ value, onChange, disabled, label = "Amount" }: { value: number; onChange: (c: number) => void; disabled?: boolean; label?: string }) {
  const [neg, setNeg] = useState(value < 0);
  // An outside change (a reset, a picked item) shows its own sign; zero keeps the one chosen.
  useEffect(() => {
    if (value !== 0) setNeg(value < 0);
  }, [value]);
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        disabled={disabled}
        aria-label={neg ? "Make positive" : "Make negative"}
        title={neg ? "A credit (negative). Select to make it positive." : "Select to make it a credit (negative)."}
        onClick={() => {
          setNeg(!neg);
          onChange(-value);
        }}
        className="h-9 w-7 shrink-0 rounded-md border border-input text-sm"
      >
        {neg ? "−" : "+"}
      </button>
      <MoneyInput label={label} disabled={disabled} value={Math.abs(value)} onChange={(c) => onChange(neg ? -c : c)} />
    </div>
  );
}

export function AccountSelect({ accounts, value, onChange, label, disabled }: { accounts: Account[]; value: string; onChange: (v: string) => void; label: string; disabled?: boolean }) {
  const groups = useMemo(() => {
    const m = new Map<string, Account[]>();
    for (const a of accounts) m.set(a.accountType, [...(m.get(a.accountType) ?? []), a]);
    return [...m.entries()];
  }, [accounts]);
  return (
    <select aria-label={label} disabled={disabled} className={cn(field, "w-full min-w-0")} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose an account</option>
      {groups.map(([type, list]) => (
        <optgroup key={type} label={type}>
          {list.map((a) => (
            <option key={a.qboId} value={a.qboId}>
              {a.fullyQualifiedName}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

// --- Register ------------------------------------------------------------------------------------

const isUncategorized = (category: string) => /^(Uncategorized|Ask My Accountant)/i.test(category);

export function BookkeepingRegister({ clientId, me }: { clientId: string; me: Me }) {
  const [range, setRange] = useState(monthBounds(-1));
  const [account, setAccount] = useState("");
  const [type, setType] = useState("");
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<{ entity: Entity; id: string } | null>(null);
  const [onlyUncategorized, setOnlyUncategorized] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState("");
  const { toast } = useToast();
  const canEdit = hasPermission(me, "engagements.write");
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);
  const accounts = useAccounts(clientId);
  const params = new URLSearchParams({ from: range.from, to: range.to, ...(account ? { account } : {}), ...(type ? { type } : {}), ...(q ? { q } : {}) });
  const { data, isLoading, error, isFetching } = useQuery<Register>({ queryKey: [`/api/clients/${clientId}/books/register?${params}`], placeholderData: (p) => p, retry: false });
  const months = Array.from({ length: 13 }, (_, i) => monthBounds(-i));
  const monthValue = months.find((m) => m.from === range.from && m.to === range.to)?.from ?? "custom";
  // Another period or filter: start the selection again.
  useEffect(() => setPicked(new Set()), [range.from, range.to, account, type, q, onlyUncategorized]);
  const rows = (data?.rows ?? []).filter((r) => !onlyUncategorized || isUncategorized(r.split));
  /** Rows that can be recategorized together: one category line is checked on the server. */
  const pickable = (r: Row) => canEdit && !!r.txnId && (r.entity === "Purchase" || r.entity === "Deposit" || r.entity === "Bill") && !data?.closedMonths.includes(r.date.slice(0, 7));
  const keyOf = (r: Row) => `${r.entity}:${r.txnId}`;
  const bulk = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send<{ updated: number; skipped: Array<{ txnId: string; reason: string }> }>("POST", `/api/clients/${clientId}/books/recategorize`, {
        accountId: target,
        items: [...picked].map((k) => ({ entity: k.split(":")[0], txnId: k.split(":")[1] })),
      }),
    onSuccess: (r) => {
      setPicked(new Set());
      toast({
        title: `${r.updated} transaction${r.updated === 1 ? "" : "s"} recategorized in QuickBooks`,
        description: r.skipped.length ? `Not changed: ${r.skipped.map((x) => `#${x.txnId} (${x.reason})`).join("; ")}` : undefined,
        variant: r.skipped.length ? "destructive" : undefined,
      });
    },
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2 border-b border-border pb-3">
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">Period</span>
          <select aria-label="Month" className={field} value={monthValue} onChange={(e) => e.target.value !== "custom" && setRange(months.find((m) => m.from === e.target.value)!)}>
            {months.map((m) => (
              <option key={m.from} value={m.from}>
                {new Date(`${m.from}T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </option>
            ))}
            <option value="custom">Custom dates</option>
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">From</span>
          <input type="date" aria-label="From" className={field} value={range.from} onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })} />
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">To</span>
          <input type="date" aria-label="To" className={field} value={range.to} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })} />
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">Account</span>
          <select aria-label="Account" className={cn(field, "max-w-[14rem]")} value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">All accounts</option>
            {accounts.map((a) => (
              <option key={a.qboId} value={a.fullyQualifiedName}>
                {a.fullyQualifiedName}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">Type</span>
          <select aria-label="Type" className={field} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {[...new Set([...(data?.types ?? []), ...(type ? [type] : [])])].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <div className="relative min-w-[12rem] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input aria-label="Search" placeholder="Name, memo, number or amount" className="pl-8" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <label className="flex h-9 items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyUncategorized} onChange={(e) => setOnlyUncategorized(e.target.checked)} /> Uncategorized only
        </label>
        {canEdit && <SuggestCategoriesButton clientId={clientId} from={range.from} to={range.to} />}
      </div>
      {picked.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span className="font-medium">{picked.size} selected</span>
          <span className="text-muted-foreground">Recategorize to</span>
          <div className="w-64 max-w-full">
            <AccountSelect accounts={accounts} value={target} onChange={setTarget} label="New category" />
          </div>
          <Button size="sm" disabled={!target || bulk.isPending} onClick={() => bulk.mutate()}>
            {bulk.isPending ? "Saving to QuickBooks…" : "Recategorize"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
            Clear
          </Button>
          {bulk.error && <span className="w-full text-destructive">{errorMessage(bulk.error)}</span>}
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !rows.length ? (
        <Empty>{data.total ? "No transactions match these filters." : "No transactions were posted in this period."}</Empty>
      ) : (
        <div className={cn("space-y-2", isFetching && "opacity-80")}>
          <p className="text-xs text-muted-foreground">
            {data.count.toLocaleString()} transaction{data.count === 1 ? "" : "s"}
            {data.count !== data.total && ` of ${data.total.toLocaleString()}`} · from QuickBooks as of {formatDateTime(data.asOf)}. Open a transaction to recategorize, split or attach a receipt.
            {data.truncated && " Showing the first 2,000; narrow the filters to see the rest."}
          </p>
          {/* Phones: one card per transaction. */}
          <ul className="divide-y divide-border rounded-lg border border-border md:hidden">
            {rows.map((r, i) => {
              const can = !!r.entity && !!r.txnId;
              return (
                <li key={`m-${r.txnId ?? "x"}-${i}`}>
                  <button type="button" disabled={!can} onClick={() => can && setOpen({ entity: r.entity!, id: r.txnId! })} className={cn("flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm", can ? "hover:bg-muted/60" : "text-muted-foreground")}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{r.name || r.type}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {formatDate(r.date)} · {r.type} · {r.split}
                      </span>
                    </span>
                    <span className="tabular shrink-0">{formatCents(r.amount)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <TableScroll label="Transactions table" className="hidden rounded-lg border border-border md:block">
            <table className="w-full min-w-[600px] text-sm">
              <thead className="bg-secondary/60 text-left text-xs text-muted-foreground">
                <tr>
                  {canEdit && (
                    <th className="w-8 px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label="Select all that can be recategorized"
                        checked={rows.some(pickable) && rows.filter(pickable).every((r) => picked.has(keyOf(r)))}
                        onChange={(e) => setPicked(e.target.checked ? new Set(rows.filter(pickable).slice(0, 100).map(keyOf)) : new Set())}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 font-medium">Date and type</th>
                  <th className="px-3 py-2 font-medium">Name and memo</th>
                  <th className="px-3 py-2 font-medium">Category and account</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r, i) => {
                  const closed = data.closedMonths.includes(r.date.slice(0, 7));
                  const can = !!r.entity && !!r.txnId;
                  const uncategorized = isUncategorized(r.split);
                  return (
                    <tr
                      key={`${r.txnId ?? "x"}-${i}`}
                      className={cn("align-top", can ? "cursor-pointer hover:bg-muted/60" : "text-muted-foreground", uncategorized && "bg-amber-50/60 dark:bg-amber-950/20")}
                      onClick={() => can && setOpen({ entity: r.entity!, id: r.txnId! })}
                      title={can ? undefined : "Opened in QuickBooks"}
                    >
                      {canEdit && (
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          {pickable(r) && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${r.name || r.type} ${formatDate(r.date)}`}
                              checked={picked.has(keyOf(r))}
                              onChange={(e) => {
                                const next = new Set(picked);
                                if (e.target.checked) {
                                  if (next.size >= 100) return;
                                  next.add(keyOf(r));
                                } else next.delete(keyOf(r));
                                setPicked(next);
                              }}
                            />
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span className="block whitespace-nowrap">
                          {formatDate(r.date)}
                          {closed && <Lock className="ml-1 inline h-3 w-3 text-muted-foreground" aria-label="Month closed" />}
                        </span>
                        {can ? (
                          <button type="button" className="text-left font-medium text-primary hover:underline" onClick={(e) => (e.stopPropagation(), setOpen({ entity: r.entity!, id: r.txnId! }))}>
                            {r.type}
                          </button>
                        ) : (
                          r.type
                        )}
                        {r.num && <span className="block text-xs text-muted-foreground">#{r.num}</span>}
                      </td>
                      <td className="max-w-[14rem] px-3 py-2">
                        <span className="block truncate">{r.name || "—"}</span>
                        {r.memo && (
                          <span className="block truncate text-xs text-muted-foreground" title={r.memo}>
                            {r.memo}
                          </span>
                        )}
                      </td>
                      <td className="max-w-[12rem] px-3 py-2">
                        <span className={cn("block truncate", uncategorized && "font-medium text-amber-800 dark:text-amber-300")}>{r.split}</span>
                        <span className="block truncate text-xs text-muted-foreground">{r.account}</span>
                      </td>
                      <td className="tabular whitespace-nowrap px-3 py-2 text-right">{formatCents(r.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        </div>
      )}
      {open && <TxnEditor clientId={clientId} me={me} entity={open.entity} id={open.id} accounts={accounts} onClose={() => setOpen(null)} />}
    </div>
  );
}

// --- Transaction editor --------------------------------------------------------------------------

function TxnEditor({ clientId, me, entity, id, accounts, onClose }: { clientId: string; me: Me; entity: Entity; id: string; accounts: Account[]; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = [`/api/clients/${clientId}/books/txn/${entity}/${id}`];
  const { data, isLoading, error } = useQuery<Txn>({ queryKey: key, retry: false });
  const { data: classes } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/classes`], staleTime: 5 * 60_000, retry: false });
  const [lines, setLines] = useState<Line[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const canEdit = hasPermission(me, "engagements.write");
  useEffect(() => {
    if (data) {
      setLines(data.lines);
      setNote(data.privateNote);
    }
  }, [data]);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<Txn>("PUT", `/api/clients/${clientId}/books/txn/${entity}/${id}`, { syncToken: data!.syncToken, privateNote: note ?? "", lines }),
    onSuccess: () => {
      toast({ title: "Saved to QuickBooks" });
      onClose();
    },
  });
  const attach = useMutation({
    meta: { inlineError: true },
    mutationFn: (f: File) => api.upload(`/api/clients/${clientId}/books/txn/${entity}/${id}/attachments`, {}, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      toast({ title: "Receipt attached", description: "Attached in QuickBooks and saved in the client's documents (Receipts)." });
    },
  });
  const activeClasses = (classes ?? []).filter((c) => c.active);
  const ro = !canEdit || !!data?.closed || !!data?.foreignCurrency;
  const je = entity === "JournalEntry";
  const sum = (lines ?? []).reduce((a, l) => a + l.amount, 0);
  const debit = (lines ?? []).filter((l) => l.postingType === "Debit").reduce((a, l) => a + l.amount, 0);
  const credit = (lines ?? []).filter((l) => l.postingType === "Credit").reduce((a, l) => a + l.amount, 0);
  const target = data ? data.lines.reduce((a, l) => a + l.amount, 0) : 0;
  const balanced = je ? debit === credit : sum === target;
  const set = (i: number, patch: Partial<Line>) => setLines((ls) => ls!.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {ENTITY_LABEL[entity]}
            {data?.docNumber ? ` #${data.docNumber}` : ""}
            {data?.name ? ` · ${data.name}` : ""}
          </DialogTitle>
          {data && (
            <DialogDescription>
              {formatDate(data.date)}
              {data.account && ` · ${entity === "Deposit" ? "deposited to" : entity === "Bill" ? "payable" : "paid from"} ${data.account.name}`} · {formatCents(data.total)}
            </DialogDescription>
          )}
        </DialogHeader>
        {isLoading || (data && !lines) ? (
          <Loading />
        ) : error || !data ? (
          <Empty>{errorMessage(error)}</Empty>
        ) : (
          <div className="space-y-4">
            {data.closed && <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">{data.date.slice(0, 7)} is closed. Reopen the close to change this transaction.</p>}
            {data.foreignCurrency && <p className="rounded-md border border-border bg-secondary px-3 py-2 text-sm">This transaction is in {data.foreignCurrency}. Edit it in QuickBooks.</p>}
            {data.otherLines > 0 && <p className="text-xs text-muted-foreground">{data.otherLines} product or service line{data.otherLines > 1 ? "s are" : " is"} kept as they are; edit those in QuickBooks.</p>}
            <TableScroll label="Transaction lines table">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr className="border-b border-border">
                    {je && <th className="w-28 py-2 pr-2 font-medium">Debit / Credit</th>}
                    <th className="py-2 pr-2 font-medium">{je ? "Account" : "Category"}</th>
                    <th className="py-2 pr-2 font-medium">Description</th>
                    {activeClasses.length > 0 && <th className="w-40 py-2 pr-2 font-medium">Class</th>}
                    <th className="w-36 py-2 pr-2 text-right font-medium">Amount</th>
                    <th className="w-10">
                      <span className="sr-only">Remove</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines!.map((l, i) => (
                    <tr key={i} className="align-top">
                      {je && (
                        <td className="py-1.5 pr-2">
                          <select aria-label="Debit or credit" disabled={ro} className={cn(field, "w-full")} value={l.postingType} onChange={(e) => set(i, { postingType: e.target.value as "Debit" })}>
                            <option value="Debit">Debit</option>
                            <option value="Credit">Credit</option>
                          </select>
                        </td>
                      )}
                      <td className="py-1.5 pr-2">
                        <AccountSelect accounts={accounts} value={l.accountId} label={`Line ${i + 1} account`} disabled={ro} onChange={(v) => set(i, { accountId: v })} />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input aria-label={`Line ${i + 1} description`} disabled={ro} className="h-9" value={l.description} onChange={(e) => set(i, { description: e.target.value })} />
                      </td>
                      {activeClasses.length > 0 && (
                        <td className="py-1.5 pr-2">
                          <select aria-label={`Line ${i + 1} class`} disabled={ro} className={cn(field, "w-full")} value={l.classId ?? ""} onChange={(e) => set(i, { classId: e.target.value || null })}>
                            <option value="">No class</option>
                            {activeClasses.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td className="py-1.5 pr-2">
                        <MoneyInput label={`Line ${i + 1} amount`} disabled={ro} value={l.amount} onChange={(c) => set(i, { amount: c })} />
                      </td>
                      <td className="py-1.5">
                        {!ro && lines!.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" aria-label={`Remove line ${i + 1}`} onClick={() => setLines((ls) => ls!.filter((_, j) => j !== i))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <div className="flex flex-wrap items-center justify-between gap-2">
              {!ro ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // A new line takes whatever is left, so a split starts out balanced.
                    const left = je ? Math.abs(debit - credit) : target - sum;
                    setLines((ls) => [...ls!, { id: null, description: "", amount: Math.max(0, left), accountId: "", classId: null, ...(je ? { postingType: debit > credit ? "Credit" : "Debit" } : {}) }]);
                  }}
                >
                  <Plus className="h-4 w-4" /> {je ? "Add line" : "Split"}
                </Button>
              ) : (
                <span />
              )}
              <p className={cn("text-sm tabular", balanced ? "text-muted-foreground" : "font-medium text-destructive")}>
                {je ? `Debits ${formatCents(debit)} · Credits ${formatCents(credit)}` : `Lines ${formatCents(sum)} of ${formatCents(target)}${balanced ? "" : ` (${sum > target ? "over" : "short"} by ${formatCents(Math.abs(target - sum))})`}`}
              </p>
            </div>
            {data.receipts.length > 0 && (
              <div className="text-sm">
                <p className="text-xs font-medium text-muted-foreground">Receipts attached from ClientFlow</p>
                <ul className="mt-1 space-y-0.5">
                  {data.receipts.map((r) => (
                    <li key={r.documentId}>
                      <a className="text-primary hover:underline" href={`/api/documents/${r.documentId}/download`}>
                        {r.filename}
                      </a>
                      <span className="text-xs text-muted-foreground"> · {formatDateTime(r.at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="txn-note">Memo (internal, saved in QuickBooks)</Label>
              <Textarea id="txn-note" rows={2} disabled={ro} value={note ?? ""} onChange={(e) => setNote(e.target.value)} />
            </div>
            {(save.error || attach.error) && <p className="text-sm text-destructive">{errorMessage(save.error ?? attach.error)}</p>}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {data && (
              <Button variant="ghost" asChild>
                <a href={data.link} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" /> Open in QuickBooks
                </a>
              </Button>
            )}
            {data && canEdit && <UploadButton onFile={(f) => attach.mutate(f)} disabled={attach.isPending} label={attach.isPending ? "Attaching…" : "Attach receipt"} />}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {ro ? "Close" : "Cancel"}
            </Button>
            {!ro && (
              <Button
                disabled={save.isPending || !balanced || (lines ?? []).some((l) => !l.accountId || l.amount <= 0)}
                onClick={() => {
                  qc.removeQueries({ queryKey: key });
                  save.mutate();
                }}
              >
                Save to QuickBooks
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Lists ---------------------------------------------------------------------------------------

type Kind = "accounts" | "vendors" | "customers" | "items" | "classes" | "locations";
const KINDS: Array<[Kind, string, string]> = [
  ["accounts", "Chart of Accounts", "account"],
  ["vendors", "Vendors", "vendor"],
  ["customers", "Customers", "customer"],
  ["items", "Products & Services", "product or service"],
  ["classes", "Classes", "class"],
  ["locations", "Locations", "location"],
];
/** Accounts QuickBooks keeps for itself; it refuses to make them inactive. */
const SYSTEM_ACCOUNTS = new Set(["AccountsPayable", "AccountsReceivable", "RetainedEarnings", "OpeningBalanceEquity", "UndepositedFunds"]);
const ACCOUNT_TYPES = ["Expense", "Other Expense", "Cost of Goods Sold", "Income", "Other Income", "Other Current Asset", "Fixed Asset", "Other Asset", "Other Current Liability", "Long Term Liability", "Equity"];

export function BookkeepingLists({ clientId, me }: { clientId: string; me: Me }) {
  const [kind, setKind] = useState<Kind>("accounts");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<ListRow | "new" | null>(null);
  const canEdit = hasPermission(me, "engagements.write");
  const { data, isLoading, error } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/${kind}`], retry: false });
  const toggle = useMutation({
    meta: { inlineError: true },
    mutationFn: (r: ListRow) => api.send("PATCH", `/api/clients/${clientId}/books/lists/${kind}/${r.id}`, { syncToken: r.syncToken, active: !r.active }),
  });
  const noun = KINDS.find((k) => k[0] === kind)![2];
  const rows = (data ?? []).filter((r) => showInactive || r.active);
  const money = (v: unknown) => (typeof v === "number" ? formatCents(Math.round(v * 100)) : "—");
  const columns: Array<[string, (r: ListRow) => string, boolean?]> =
    kind === "accounts"
      ? [
          ["Type", (r) => String(r.detail.type ?? "")],
          ["Number", (r) => String(r.detail.number ?? "")],
          ["Balance", (r) => money(r.detail.balance), true],
        ]
      : kind === "vendors"
        ? [
            ["Email", (r) => String(r.detail.email ?? "")],
            ["1099", (r) => (r.detail.is1099 ? "Yes" : "")],
            ["Balance", (r) => money(r.detail.balance), true],
          ]
        : kind === "customers"
          ? [
              ["Email", (r) => String(r.detail.email ?? "")],
              ["Phone", (r) => String(r.detail.phone ?? "")],
              ["Balance", (r) => money(r.detail.balance), true],
            ]
          : kind === "items"
            ? [
                ["Type", (r) => String(r.detail.type ?? "")],
                ["Income account", (r) => String(r.detail.incomeAccount ?? "")],
                ["Price", (r) => money(r.detail.price), true],
              ]
            : [];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented label="List" value={kind} onChange={setKind} options={KINDS.map(([k, l]) => ({ value: k, label: l }))} />
        <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
        </label>
        {canEdit && (
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" /> Add {noun}
          </Button>
        )}
      </div>
      {toggle.error && <p className="text-sm text-destructive">{errorMessage(toggle.error)}</p>}
      {isLoading ? (
        <Loading />
      ) : error ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !rows.length ? (
        <Empty>Nothing in this list yet.</Empty>
      ) : (
        <TableScroll label="List entries table" className="rounded-lg border border-border">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-secondary/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                {columns.map(([h, , right]) => (
                  <th key={h} className={cn("px-3 py-2 font-medium", right && "text-right")}>
                    {h}
                  </th>
                ))}
                <th className="px-3 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className={cn(!r.active && "text-muted-foreground")}>
                  <td className="px-3 py-2 font-medium">
                    {r.name}
                    {!r.active && <span className="ml-2 rounded bg-muted px-1.5 text-[11px] font-normal">Inactive</span>}
                  </td>
                  {columns.map(([h, f, right]) => (
                    <td key={h} className={cn("px-3 py-2", right && "tabular text-right")}>
                      {f(r)}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {canEdit && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                          Edit
                        </Button>
                        {!(kind === "accounts" && SYSTEM_ACCOUNTS.has(String(r.detail.subType))) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={toggle.isPending}
                          onClick={async () => {
                            if (!r.active || (await ask({ title: `Make “${r.name}” inactive?`, description: "It stays on past transactions but can't be picked for new ones. You can make it active again later.", confirmLabel: "Make inactive" }))) toggle.mutate(r);
                          }}
                        >
                          {r.active ? "Make inactive" : "Make active"}
                        </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {editing && <ListEntryDialog clientId={clientId} kind={kind} noun={noun} entry={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ListEntryDialog({ clientId, kind, noun, entry, onClose }: { clientId: string; kind: Kind; noun: string; entry: ListRow | null; onClose: () => void }) {
  const accounts = useAccounts(clientId);
  const d = entry?.detail ?? {};
  const [v, setV] = useState({
    name: entry?.name ?? "",
    type: String(d.type ?? (kind === "items" ? "Service" : "Expense")),
    number: String(d.number ?? ""),
    description: String(d.description ?? ""),
    company: String(d.company ?? ""),
    email: String(d.email ?? ""),
    phone: String(d.phone ?? ""),
    is1099: d.is1099 === true,
    price: typeof d.price === "number" ? Math.round(d.price * 100) : 0,
    incomeAccountId: accounts.find((a) => a.fullyQualifiedName === d.incomeAccount)?.qboId ?? "",
  });
  const fields = (): Record<string, unknown> => {
    switch (kind) {
      case "accounts":
        return { name: v.name, type: v.type, number: v.number || undefined, description: v.description || undefined };
      case "vendors":
        return { name: v.name, company: v.company || undefined, email: v.email || undefined, phone: v.phone || undefined, is1099: v.is1099 };
      case "customers":
        return { name: v.name, company: v.company || undefined, email: v.email || undefined, phone: v.phone || undefined };
      case "items":
        return { name: v.name, type: v.type, description: v.description || undefined, price: v.price / 100, incomeAccountId: v.incomeAccountId };
      default:
        return { name: v.name };
    }
  };
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => (entry ? api.send("PATCH", `/api/clients/${clientId}/books/lists/${kind}/${entry.id}`, { syncToken: entry.syncToken, fields: fields() }) : api.send("POST", `/api/clients/${clientId}/books/lists/${kind}`, fields())),
    onSuccess: onClose,
  });
  const input = (k: keyof typeof v, label: string, type = "text") => (
    <div className="space-y-1">
      <Label htmlFor={`le-${k}`}>{label}</Label>
      <Input id={`le-${k}`} type={type} value={String(v[k])} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
    </div>
  );
  const incomeAccounts = accounts.filter((a) => a.accountType === "Income" || a.accountType === "Other Income");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{entry ? `Edit ${noun}` : `Add ${noun}`}</DialogTitle>
          <DialogDescription>Saved straight to the client's QuickBooks.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {input("name", "Name")}
          {kind === "accounts" && (
            <>
              <div className="space-y-1">
                <Label htmlFor="le-type">Account type</Label>
                <select id="le-type" className={cn(field, "w-full")} value={v.type} disabled={!!entry} onChange={(e) => setV({ ...v, type: e.target.value })}>
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
                {!entry && <p className="text-xs text-muted-foreground">Bank and credit card accounts are added in QuickBooks, where they connect to the bank feed.</p>}
              </div>
              {input("number", "Account number (optional)")}
              {input("description", "Description (optional)")}
            </>
          )}
          {(kind === "vendors" || kind === "customers") && (
            <>
              {input("company", "Company (optional)")}
              {input("email", "Email (optional)", "email")}
              {input("phone", "Phone (optional)")}
              {kind === "vendors" && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={v.is1099} onChange={(e) => setV({ ...v, is1099: e.target.checked })} /> Track payments for Form 1099
                </label>
              )}
            </>
          )}
          {kind === "items" && (
            <>
              <div className="space-y-1">
                <Label htmlFor="le-type">Type</Label>
                <select id="le-type" className={cn(field, "w-full")} value={v.type} disabled={!!entry} onChange={(e) => setV({ ...v, type: e.target.value })}>
                  <option value="Service">Service</option>
                  <option value="NonInventory">Non-inventory product</option>
                </select>
              </div>
              {input("description", "Description (optional)")}
              <div className="space-y-1">
                <Label>Price</Label>
                <MoneyInput label="Price" value={v.price} onChange={(c) => setV({ ...v, price: c })} />
              </div>
              <div className="space-y-1">
                <Label>Income account</Label>
                <AccountSelect accounts={incomeAccounts} value={v.incomeAccountId} label="Income account" onChange={(x) => setV({ ...v, incomeAccountId: x })} />
              </div>
            </>
          )}
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending || !v.name.trim() || (kind === "items" && !v.incomeAccountId)} onClick={() => save.mutate()}>
            {entry ? "Save changes" : `Add ${noun}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Change log ----------------------------------------------------------------------------------

export function QboChanges({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery<Array<{ id: string; action: string; summary: string; userId: string | null; createdAt: string }>>({ queryKey: [`/api/clients/${clientId}/books/changes`], retry: false });
  const names = useMemberNames();
  return (
    <Section title="Changes made from ClientFlow" description="Everything staff changed in this client's QuickBooks from ClientFlow, newest first.">
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>Nothing has been changed from ClientFlow yet.</Empty>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {data.slice(0, 25).map((c) => (
            <li key={c.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
              <span className="flex-1">{c.summary}</span>
              <span className="text-xs text-muted-foreground">
                {c.userId ? (names.get(c.userId) ?? "Staff") : "ClientFlow"} · {formatDateTime(c.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
